import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Attachment, Note, Reference } from '../../shared/models';
import { CONFIG } from '../../config';
import Swal from 'sweetalert2';
import { TemplateModalService } from './template-modal.service';
import { TranslateService } from '@ngx-translate/core';


@Injectable()
export class TextContentService {
  get editMode(): boolean {
    return this._editMode;
  }

  set editMode(value: boolean) {
    this._editMode = value;
  }

  /**
   * Handle state to disable global elements
   */
  private _editMode = false;
  private absUrlWarnMessage = this.translateService.instant('ERRORS.ABSOLUTE_IMAGE_URL_WARNING');
  public saveDraftNote$: Subject<{ selectedNote: Note, editorContent?: { content: string, isOffline: boolean }, clickEvt: MouseEvent }> =
    new Subject<{ selectedNote: Note, editorContent?: { content: string, isOffline: boolean }, clickEvt: MouseEvent }>();
  public updateNote$: Subject<{ currentNote: Note, editorContent: string }> = new Subject<{ currentNote: Note, editorContent: string }>();
  public openReferenceModal$: Subject<{ currentNoteId: number, references: Array<any>, textContentId: number, isCrmNote?: boolean }> =
    new Subject<{ currentNoteId: number, references: Array<any>, textContentId: number }>();
  public reviewNote$: Subject<{ complianceReviewComment: string, selectedNote: Note }> =
    new Subject<{ complianceReviewComment: string, selectedNote: Note }>();
  public thesisObjReferenceEdited$: Subject<{ references: Array<Reference> }> = new Subject<{ references: Array<Reference> }>();
  public noteObjReferenceEdited$: Subject<{ references: Array<Reference> }> = new Subject<{ references: Array<Reference> }>();
  public discardNoteDraft$: Subject<number> = new Subject<number>();
  public updateAttachmentList$: Subject<Array<Attachment>> = new Subject<Array<Attachment>>();
  public openAssociatedMapModal$ = new Subject<any>();

  constructor(
    private templateModalService: TemplateModalService,
    private translateService: TranslateService
  ) { }

  /**
   * Notifies the listeners to save as draft the selected note
   * @param selectedNote
   * @param editorContent
   * @param clickEvt
   */
  public saveDraftNote(selectedNote: Note, editorContent?: { content: string, isOffline: boolean }, clickEvt?: MouseEvent): void {
    this.saveDraftNote$.next({ selectedNote, editorContent, clickEvt });
  }

  /**
   * Notifies the listeners to update the selected note
   * @param data
   */
  public updateNote(data: { currentNote: Note, editorContent: string }): void {
    this.updateNote$.next(data);
  }

  /**
   * Notifies the listeners to open the reference modal
   * @param data
   */
  public openReferenceModal(data: { currentNoteId: number, references: Array<any>, textContentId: number, isCrmNote?: boolean}): void {
    this.openReferenceModal$.next(data);
  }

  /**
   * Pass review data for notes to listeners
   * @param data
   */
  public reviewNotePosted(data: { complianceReviewComment: string, selectedNote: Note }) {
    this.reviewNote$.next(data);
  }

  /**
   * Notifies when the thesis references are updated
   * @param data
   */
  public thesisObjReferenceEdited(data: { references: Array<Reference> }): void {
    this.thesisObjReferenceEdited$.next(data);
  }

  /**
   * Notifies when the notes references are updated
   * @param data
   */
  public noteObjReferenceEdited(data: { references: Array<any>, noteId?: number }): void {
    this.noteObjReferenceEdited$.next(data);
  }

  /**
   * Force close object references modal from anywhere it is used
   */
  public closeReferencesModal(): void {
    const closeBtn = document.getElementById('closeReferenceModalBtn') as HTMLButtonElement;
    if (closeBtn) {
      closeBtn.click(); // closing this way because the modal ref prop is missing between the multiple ways to display the modal form
    }
  }

  /**
   * Notifies the company parent about discard note draft
   * @param noteId
   */
  public discardNoteDraft(noteId: number): void {
    this.discardNoteDraft$.next(noteId);
  }

  /**
   * Passes the attachment ids to the parent component on upload file
   * @param file
   */
  public updateAttachmentList(file: Array<Attachment>): void {
    this.updateAttachmentList$.next(file);
  }

  /**
   * Trigger the opening of the associated map modal
   */
  public openAssociatedMapModal(id: number): void {
    this.openAssociatedMapModal$.next(id);
  }

  public checkAbsoluteImgSrc(content: string): void {
    const pattern = new RegExp(CONFIG.absoluteImageSrc, 'i');
    const patternDotCom = new RegExp(/(src=")(https?:\/\/)(\S*\.com).*"\s/, 'i');
    if (pattern.test(content) || patternDotCom.test(content)) {
      Swal.fire(this.templateModalService.genericAlert('', this.absUrlWarnMessage)).catch(() => {});
    }
  }
}
